import i18next from 'i18next';
import { Config, Layout } from 'plotly.js';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Plot from 'react-plotly.js';
import useMediaQuery from 'src/common/hooks/use-media-query';
import { Languages } from 'src/i18n';
import { plotlyLocales } from 'src/locales/plotly-locales';
import { OrganizationStatisticsChartKey, OrganizationStatisticsChartPoint } from 'src/stores/statistics-store';

import { theme } from 'antd';
const { useToken } = theme;

const OrganizationStatisticsChart: FC<{
  createdData: OrganizationStatisticsChartPoint[];
  updatedData?: OrganizationStatisticsChartPoint[];
  chartKey: OrganizationStatisticsChartKey;
}> = ({ createdData, updatedData, chartKey }) => {
  const { t } = useTranslation();
  const onMobile = useMediaQuery('(max-width: 576px)');
  const currentLanguage = i18next.language !== Languages.Portuguese ? i18next.language : 'pt-br';
  const { token } = useToken();
  const textColor = token.colorText;
  const bgColor = token.colorBgContainer;
  const gridColor = token.colorTextTertiary;

  const [layout, setLayout] = useState<Partial<Layout>>({});

  useEffect(() => {
    const newLayout = generateLayout();
    setLayout(newLayout);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const generateLayout = () => {
    const plotLayout: Partial<Layout> = {
      paper_bgcolor: bgColor,
      plot_bgcolor: bgColor,
      title: {
        text: `${t(chartKey)}`,
        font: {
          size: onMobile ? 14 : 16,
          color: textColor,
        },
      },
      showlegend: true,
      legend: { x: 0, y: -0.15, orientation: 'h', groupclick: 'toggleitem', tracegroupgap: 20, font: { color: textColor } },
      yaxis: {
        tickfont: { color: textColor },
        gridcolor: gridColor,
        dtick: maxY < 10 ? 1 : undefined,
        tickformat: ',d',
      },
      xaxis: {
        tickfont: { color: textColor },
        gridcolor: gridColor,
        showticklabels: !(createdData.length === 0 && (!updatedData || updatedData.length === 0)),
        tickformat: '%d/%m/%Y',
      },
    };

    return plotLayout;
  };

  let traceData = [];

  const maxY = Math.max(
    createdData.reduce((prev, curr) => ({ count: Math.max(prev.count, curr.count), date: '' }), { count: 0, date: '' }).count,
    (updatedData ?? []).reduce((prev, curr) => ({ count: Math.max(prev.count, curr.count), date: '' }), { count: 0, date: '' }).count
  );

  const createdTrace = {
    name: t('CreationChartLegend'),
    x: createdData.length > 0 ? createdData.map((item) => item.date) : [],
    y: createdData.length > 0 ? createdData.map((item) => item.count) : [],
    mode: 'lines+markers',
    tickformat: '.2f',
    line: {
      color: '#3cb44b',
      width: 3,
    },
  };

  traceData.push(createdTrace);

  if (updatedData) {
    const updatedTrace = {
      name: t('UpdateChartLegend'),
      x: updatedData.length > 0 ? updatedData.map((item) => item.date) : [],
      y: updatedData.length > 0 ? updatedData.map((item) => item.count) : [],
      mode: 'lines+markers',
      tickformat: '.2f',
      line: {
        color: '#0082c8',
        width: 3,
      },
    };
    traceData.push(updatedTrace);
  }

  const config: Partial<Config> = {
    displaylogo: false,
    toImageButtonOptions: {
      filename: 'chart',
      width: 800,
      height: 600,
      format: 'png',
    },
    locale: currentLanguage,
    locales: plotlyLocales,
  };

  return <Plot data={traceData} layout={layout} config={config} useResizeHandler={true} style={{ width: '100%', height: '100%' }} />;
};

export default OrganizationStatisticsChart;
